import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { UserRow } from "@/components/user-row";
import { UserIdentityConfirmationDialog } from "@/components/user-identity-confirmation-dialog";
import { Loader2, User, Crown } from "lucide-react";
import type { DocumentUser, InsertDocumentUser, User as OrgUser } from "@shared/schema";
import { insertDocumentUserSchema } from "@shared/schema";
import { useIsAdmin } from "@/hooks/useIsAdmin";

interface DocumentAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: DocumentUser[];
  isLoadingUsers: boolean;
  onLogin: (user: DocumentUser) => void;
  onCreateUser: (userData: Omit<InsertDocumentUser, 'documentId'>) => Promise<void>;
  documentColor: string;
  documentOrganizationId?: string;
}

const userFormSchema = insertDocumentUserSchema.omit({ documentId: true }).extend({
  name: z.string().min(1, "Name is required").max(50, "Name too long"),
  email: z.string().email("Valid email required").max(100, "Email too long"),
});

type UserFormData = z.infer<typeof userFormSchema>;

export function DocumentAuthModal({
  isOpen,
  onClose,
  users,
  isLoadingUsers,
  onLogin,
  onCreateUser,
  documentColor,
  documentOrganizationId,
}: DocumentAuthModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [pendingUser, setPendingUser] = useState<DocumentUser | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { isManagerOrAdmin, user: currentOrgUser } = useIsAdmin();

  // Fetch organization users for admin suggestions
  const { data: orgUsers = [], isLoading: isLoadingOrgUsers } = useQuery<OrgUser[]>({
    queryKey: ['/api/organizations', documentOrganizationId, 'users'],
    enabled: isManagerOrAdmin && !!documentOrganizationId,
  });

  // Filter org users to suggest admin/manager names not already in document
  const adminSuggestions = isManagerOrAdmin 
    ? orgUsers
        .filter(orgUser => ['admin', 'manager'].includes(orgUser.role))
        .filter(orgUser => !users.some(docUser => docUser.email === orgUser.email))
        .map(orgUser => ({
          id: '', // Will be generated on creation
          name: `${orgUser.firstName} ${orgUser.lastName}`.trim(),
          email: orgUser.email || '',
          documentId: '', // Will be set on creation
          createdAt: new Date(),
          active: true,
          lastSeenAt: null,
        }) as DocumentUser)
    : [];

  // Add URL parameter parsing
  const urlParams = new URLSearchParams(window.location.search);
  const inviteEmail = urlParams.get('inviteEmail');

  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      name: "",
      email: inviteEmail || "", // Pre-populate from URL
    },
  });

  const handleUserSelect = (user: DocumentUser) => {
    setPendingUser(user);
    setShowConfirmation(true);
  };

  const handleConfirmIdentity = () => {
    if (pendingUser) {
      onLogin(pendingUser);
      setShowConfirmation(false);
      setPendingUser(null);
    }
  };

  const handleDeclineIdentity = () => {
    setShowConfirmation(false);
    setPendingUser(null);
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      // Check for duplicate email addresses
      const existingDocUser = users.find(u => u.email === data.email);
      const existingOrgUser = orgUsers.find(u => u.email === data.email);
      
      if (existingDocUser) {
        form.setError("email", { message: "You've already joined, please select your name above" });
        return;
      }
      
      if (existingOrgUser) {
        form.setError("email", { message: "You're already here, please click above" });
        return;
      }
      
      setIsCreating(true);
      await onCreateUser(data);
      form.reset();
    } catch (error) {
      console.error("Failed to create user:", error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <UserIdentityConfirmationDialog
        isOpen={showConfirmation}
        user={pendingUser}
        onConfirm={handleConfirmIdentity}
        onDecline={handleDeclineIdentity}
        documentColor={documentColor}
      />

      <Dialog open={isOpen && !showConfirmation} onOpenChange={() => {}}>
        <DialogContent className="w-full max-w-[min(100vw-1.5rem,26rem)] sm:max-w-md max-h-[90vh] p-4 sm:p-6 rounded-md overflow-hidden [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-center">Join Document</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-center text-muted-foreground">
              Select your name from the list below or create a new user profile to join this document
            </DialogDescription>
          </DialogHeader>

        <div className="space-y-4 sm:space-y-6 overflow-hidden">
          {/* Existing Users Section - includes admin suggestions when applicable */}
          <div className="space-y-3 sm:space-y-4 overflow-hidden">
            <h3 className="font-semibold text-center text-sm sm:text-base">I'm already here</h3>
            
            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-muted-foreground">
                <User className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 opacity-50" />
                <p className="text-xs sm:text-sm">No users have joined this document yet.</p>
                <p className="text-xs">Be the first to join!</p>
              </div>
            ) : (
              <div className="h-36 sm:h-44 w-full overflow-y-auto overflow-x-hidden overscroll-contain">
                <div className="space-y-1.5 pr-1">
                  {/* Render admin suggestions first (if admin viewing) */}
                  {isManagerOrAdmin && adminSuggestions.map((suggestion, index) => (
                    <UserRow
                      key={`suggestion-${index}`}
                      user={suggestion as DocumentUser}
                      onSelect={async (suggestedUser) => {
                        // Create the user first, then login
                        await onCreateUser({
                          name: suggestedUser.name,
                          email: suggestedUser.email,
                        });
                      }}
                      showClickable={true}
                      isAdmin={true}
                      documentColor={documentColor}
                      showNotJoinedYet={true}
                    />
                  ))}
                  
                  {/* Render existing document users */}
                  {users.map((user) => {
                    // Check if this user is an admin/manager from org users
                    const isUserAdmin = isManagerOrAdmin && orgUsers.some(orgUser => 
                      orgUser.email === user.email && ['admin', 'manager'].includes(orgUser.role)
                    );
                    
                    return (
                      <UserRow
                        key={user.id}
                        user={user}
                        onSelect={handleUserSelect}
                        showClickable={true}
                        isAdmin={isUserAdmin}
                        documentColor={documentColor}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Separator />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="bg-background px-2 text-xs text-muted-foreground">OR</span>
            </div>
          </div>

          {/* New User Section - Now secondary and less prominent */}
          <div className="space-y-2.5 sm:space-y-3 opacity-75 overflow-hidden">
            <h3 className="font-medium text-center text-sm sm:text-base text-foreground">I'm someone new</h3>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2.5 sm:space-y-3">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Enter your name"
                          {...field}
                          className="min-h-[40px] text-sm w-full"
                          data-testid="input-join-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          {...field}
                          className="min-h-[40px] text-sm w-full"
                          data-testid="input-join-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  disabled={isCreating}
                  className="w-full min-h-[40px] text-sm text-white border-0 hover:opacity-90 opacity-90"
                  style={{ backgroundColor: documentColor }}
                  data-testid="button-join-document"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    "Join Document"
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}